import { DiToken } from '@bcf-vanilla-ts-v1-shared/di/di-token';
import { bcfRouterRouteMatcher } from '../bcf-router/bcf-router-route-matcher';
import { calculatePathForChild } from '../bcf-router/pure-utils/calculate-path-for-child';
import type { BcfRoute, Params, ResolvedBcfRoute } from '../bcf-router/types';

export type RoutePreloadId = NonNullable<BcfRoute['preload']>[number];

/** Factories, not built arrays, so naming a chain at module scope does not build every table. */
export type BcfRouteTableFactory = () => BcfRoute[];

/**
 * Per-app statement of route nesting, outermost first (ADR-0016). An app that does not register
 * it gets an empty chain, so `BcfActivatedRoute.data` falls back to router memory only.
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
export const APP_ROUTE_CHAIN: DiToken<BcfRouteTableFactory[]> = new DiToken<BcfRouteTableFactory[]>('appRouteChain', {
  factory: () => []
});

type RouteData = Record<string, any>;

export type RoutePreloadMatch = {
  ids: RoutePreloadId[];
  params: Params;
  data: RouteData;
};

/**
 * Walks the route chain `url` activates, level by level. `routeChain` is the app's own statement
 * of its nesting, outermost first. Shared by `collectRoutePreloadIds` (preload ids) and
 * `resolveRouteChainData` (merged route `data`) - one matching walk, two projections.
 *
 * A `loadRoutes` table nests INSIDE the same `bcfRouterRouteMatcher` call (it recurses
 * internally), so it is already reachable within one chain level and needs no entry of its own -
 * only a rendering parent that mounts a nested `<bcf-router>` needs a level here.
 *
 * Params and data are shallow-merged parent -> child, deepest wins.
 *
 * KNOWN GAP: a rendering parent that nests a DIFFERENT table (`'user-settings/*'`) is
 * indistinguishable from the one the chain names, so bare `/en/user-settings/` picks up the next
 * level's `path: ''` ids/data. Costs a wasted prefetch or a missed layout override, never a wrong
 * page.
 */
function walkRouteChain(routeChain: BcfRouteTableFactory[], url: string): RoutePreloadMatch {
  const ids: Set<RoutePreloadId> = new Set<RoutePreloadId>();
  let params: Params = {};
  let data: RouteData = {};
  let levelUrl: string = url;

  for (const routeTable of routeChain) {
    const resolved: ResolvedBcfRoute | undefined = bcfRouterRouteMatcher(routeTable(), levelUrl);
    if (!resolved || resolved.urlToNavigate) {
      break;
    }

    for (const id of resolved.route.preload ?? []) {
      ids.add(id);
    }
    params = { ...params, ...resolved.params };
    data = { ...data, ...(resolved.data ?? {}) };

    // Re-base the url, never the child paths: rewriting a child `path: '*'` to `'/en/*'` disables
    // its `fallthroughAvailablePaths` gate, which only applies to `route.path === '*'`.
    levelUrl = resolved.route.path === undefined ? '' : calculatePathForChild(levelUrl, resolved.route.path);
    if (levelUrl === '') {
      break;
    }
  }

  return { ids: [...ids], params: params, data: data };
}

/** Decision: see ADR-0016 - shares `walkRouteChain` with `resolveRouteChainData`. */
export function collectRoutePreloadIds(routeChain: BcfRouteTableFactory[], url: string): RoutePreloadMatch {
  return walkRouteChain(routeChain, url);
}

/**
 * The merged `data` of every route the chain activates for `url`, resolved synchronously and
 * top-down - same url, same tables, same matching walk as SSR preloading, so it agrees with the
 * server on the first render instead of only catching up once nested `<bcf-router>`s mount.
 */
export function resolveRouteChainData<T = RouteData>(routeChain: BcfRouteTableFactory[], url: string): T {
  return walkRouteChain(routeChain, url).data as T;
}

type RoutePreloadDispatch = (ids: RoutePreloadId[], params: Params) => Promise<void>;

/**
 * Returns a promise so callers can `.catch()` it, never so they can await it: awaiting on SSR was
 * measured at 1585ms against ~1000ms and rejected.
 */
export function runRoutePreload(
  routeChain: BcfRouteTableFactory[],
  url: string,
  dispatch: RoutePreloadDispatch
): Promise<void> {
  const match: RoutePreloadMatch = collectRoutePreloadIds(routeChain, url);
  if (match.ids.length === 0) {
    return Promise.resolve();
  }
  return dispatch(match.ids, match.params);
}
